import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';

export type ProductConfigValues = {
  unitsPerBox: number | null;
  boxesPerLayer: number | null;
  layers: number | null;
  unitsPerPallet: number | null;
  unitVolume: number | null;
  unitWeight: number | null;
};

export type ProductConfigPayload = {
  product_id: number;
  supplier_id: number;
  units_per_box?: number | null;
  boxes_per_base?: number | null;
  bases_per_pallet?: number | null;
  units_per_pallet?: number | null;
  unit_volume_m3?: number | null;
  unit_weight_kg?: number | null;
};

type FieldKey = keyof ProductConfigValues;

const FIELDS: Array<{ key: FieldKey; payload: keyof ProductConfigPayload; labelKey: string; step: string }> = [
  { key: 'unitsPerBox', payload: 'units_per_box', labelKey: 'loadConfigUnitsPerBox', step: '1' },
  { key: 'boxesPerLayer', payload: 'boxes_per_base', labelKey: 'loadBoxesPerLayerColumn', step: '1' },
  { key: 'layers', payload: 'bases_per_pallet', labelKey: 'loadLayersColumn', step: '1' },
  { key: 'unitsPerPallet', payload: 'units_per_pallet', labelKey: 'loadConfigUnitsPerPallet', step: '1' },
  { key: 'unitVolume', payload: 'unit_volume_m3', labelKey: 'loadConfigUnitVolume', step: 'any' },
  { key: 'unitWeight', payload: 'unit_weight_kg', labelKey: 'loadConfigUnitWeight', step: 'any' },
];

const toText = (value: number | null) => (value != null && value > 0 ? String(value) : '');

type Props = {
  productId: number | null;
  supplierId: number | null;
  values: ProductConfigValues;
  notStackable: boolean;
  productLink: string | null;
  palletLink: string | null;
  /** Guarda en el maestro (única fuente) y recalcula la estimación. Sin él, la franja es de solo lectura. */
  onSave?: (payload: ProductConfigPayload) => Promise<void>;
  formatValue: (value: number) => string;
  /** Unidad de peso de la organización y su factor desde kg: el peso se edita en esa unidad y se guarda en kg. */
  weightUnit?: string;
  weightFactor?: number;
  t: (key: string, options?: Record<string, unknown>) => string;
};

export function ProductConfigStrip({ productId, supplierId, values, notStackable, productLink, palletLink, onSave, formatValue, weightUnit = 'kg', weightFactor = 1, t }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<FieldKey, string>>(() => ({} as Record<FieldKey, string>));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canEdit = onSave != null && productId != null && supplierId != null;

  const startEditing = () => {
    setDraft(Object.fromEntries(FIELDS.map(({ key }) => [key, toText(values[key])])) as Record<FieldKey, string>);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (!onSave || productId == null || supplierId == null) return;
    const payload: ProductConfigPayload = { product_id: productId, supplier_id: supplierId };
    for (const field of FIELDS) {
      const raw = (draft[field.key] ?? '').trim().replace(',', '.');
      const original = toText(values[field.key]);
      if (raw === original) continue;
      const parsed = raw === '' ? null : Number(raw);
      if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) {
        setError(t('supply.supplyAgent.loadConfigInvalid'));
        return;
      }
      (payload as Record<string, number | null>)[field.payload] = field.key === 'unitWeight' && parsed != null ? parsed / weightFactor : parsed;
    }
    if (Object.keys(payload).length === 2) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(payload);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.loadConfigSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const linkClass = 'font-medium text-sky-700 hover:underline';

  return (
    <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
        <span className="font-semibold text-slate-700">{t('supply.supplyAgent.loadConfigTitle')}</span>
        {editing ? (
          <>
            {FIELDS.map((field) => (
              <label key={field.key} className="inline-flex items-center gap-1.5">
                <span>{t(`supply.supplyAgent.${field.labelKey}`, { unit: weightUnit })}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={field.step}
                  value={draft[field.key] ?? ''}
                  disabled={saving}
                  onChange={(event) => { const next = event.currentTarget.value; setDraft((current) => ({ ...current, [field.key]: next })); }}
                  className="h-7 w-20 rounded border border-slate-300 bg-white px-1.5 text-right text-xs tabular-nums text-slate-900 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </label>
            ))}
            <span className="inline-flex items-center gap-2">
              <button type="button" onClick={() => void save()} disabled={saving} className="rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
                {saving ? t('supply.supplyAgent.loadConfigSaving') : t('supply.supplyAgent.loadConfigSave')}
              </button>
              <button type="button" onClick={() => setEditing(false)} disabled={saving} className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                {t('supply.supplyAgent.loadConfigCancel')}
              </button>
            </span>
          </>
        ) : (
          <>
            {FIELDS.map((field) => {
              const value = values[field.key];
              return (
                <span key={field.key}>
                  {t(`supply.supplyAgent.${field.labelKey}`, { unit: weightUnit })}: <b className="font-semibold tabular-nums text-slate-900">{value != null && value > 0 ? formatValue(value) : '—'}</b>
                </span>
              );
            })}
            {notStackable ? <span className="font-medium text-amber-700">{t('supply.supplyAgent.loadConfigNotStackable')}</span> : null}
            {canEdit ? (
              <button type="button" onClick={startEditing} className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50">
                <Pencil size={11} aria-hidden="true" />
                {t('supply.supplyAgent.loadConfigEdit')}
              </button>
            ) : null}
            {productLink ? <Link to={productLink} target="_blank" rel="noopener noreferrer" className={linkClass}>{t('supply.supplyAgent.loadConfigEditProduct')}</Link> : null}
            {palletLink ? <Link to={palletLink} target="_blank" rel="noopener noreferrer" className={linkClass}>{t('supply.supplyAgent.loadConfigEditPallet')}</Link> : null}
          </>
        )}
      </div>
      {editing ? <p className="mt-1.5 text-[10px] text-slate-500">{t('supply.supplyAgent.loadConfigEditHint')}</p> : null}
      {error ? <p className="mt-1 text-[11px] text-rose-700" role="alert">{error}</p> : null}
    </div>
  );
}
